// src/utils/parseUploadedSpreadsheet.js
// Parses a real, student-uploaded .xlsx workbook (Submission Mode:
// "Spreadsheet Upload" - see StudentDashboard.jsx) into the same shape
// submissions.js's spreadsheetAnswers already expects from the exam tool
// (see Phase 1: ExamWindowApp.jsx / ExamSpreadsheetGrid.jsx), so the rest of
// the marking pipeline (FirebaseMarker.jsx, SpreadsheetCellViewer.jsx,
// generateSpreadsheetUploadPdf.js) doesn't need to know which path a
// submission came from.
//
// Deliberately does NOT run cell formulas through spreadsheetFormulas.js's
// evaluateGrid() - that engine only understands the small function set the
// exam tool's own spreadsheet needs. A real Excel file can use formulas it
// has no idea how to evaluate (VLOOKUP, INDEX/MATCH, etc.), so re-evaluating
// would silently show a wrong or #NAME? result for a perfectly valid Excel
// formula. Instead every cell uses Excel's OWN cached computed value
// (SheetJS's cell.v/cell.w, saved by Excel itself) - see precomputedEval
// below, threaded through to both the generated PDF and the interactive
// viewer so the two always agree.

import * as XLSX from 'xlsx';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // matches the existing PDF upload cap

function emptyGridCell() {
  return { value: '', bold: false, italic: false, underline: false, align: 'left', fontSize: null, fontColor: null, fillColor: null };
}

function emptyEvalCell() {
  return { display: '', isError: false, numeric: null, isBlank: true, isFormula: false };
}

function cellToGridAndEval(cell) {
  if (!cell) {
    return { gridCell: emptyGridCell(), evalCell: emptyEvalCell() };
  }

  const isFormula = !!cell.f;
  const isError = cell.t === 'e';
  const isNumeric = cell.t === 'n';
  const displayText = cell.w != null ? String(cell.w) : (cell.v != null ? String(cell.v) : '');

  const gridCell = {
    ...emptyGridCell(),
    value: isFormula ? `=${cell.f}` : displayText,
    align: isNumeric ? 'right' : 'left'
  };

  const evalCell = {
    display: displayText,
    isError,
    numeric: isNumeric ? cell.v : null,
    isBlank: cell.v == null && !isFormula,
    isFormula
  };

  return { gridCell, evalCell };
}

// Reads one worksheet's real used range (not a padded default grid) into
// { grid, precomputedEval } - the shapes renderSpreadsheetGrid (PDF) and
// SpreadsheetCellViewer (interactive) both already accept.
function parseWorksheet(worksheet) {
  const ref = worksheet['!ref'];
  if (!ref) return null;

  const range = XLSX.utils.decode_range(ref);
  const grid = [];
  const precomputedEval = [];

  for (let r = range.s.r; r <= range.e.r; r++) {
    const gridRow = [];
    const evalRow = [];
    for (let c = range.s.c; c <= range.e.c; c++) {
      const cellRef = XLSX.utils.encode_cell({ r, c });
      const { gridCell, evalCell } = cellToGridAndEval(worksheet[cellRef]);
      gridRow.push(gridCell);
      evalRow.push(evalCell);
    }
    grid.push(gridRow);
    precomputedEval.push(evalRow);
  }

  const hasContent = precomputedEval.some(row => row.some(cell => !cell.isBlank));
  if (!hasContent) return null;

  return { grid, precomputedEval };
}

/**
 * Validates and parses an uploaded workbook. Throws a user-facing Error for
 * anything that isn't a plain, non-macro .xlsx within the size cap - callers
 * should show error.message directly.
 *
 * Returns spreadsheetAnswers in the exact shape submissions.js/createSubmission
 * already accepts: { [sheetKey]: { name, gridJson, precomputedEval } }.
 */
export async function parseUploadedSpreadsheet(file) {
  const nameLower = (file.name || '').toLowerCase();
  if (nameLower.endsWith('.xlsm')) {
    throw new Error('Macro-enabled workbooks (.xlsm) are not supported. Please save as a plain .xlsx file.');
  }
  if (!nameLower.endsWith('.xlsx')) {
    throw new Error('Please upload a .xlsx spreadsheet file.');
  }
  if (file.size > MAX_FILE_SIZE) {
    throw new Error('File size must be less than 10MB.');
  }

  const arrayBuffer = await file.arrayBuffer();
  let workbook;
  try {
    workbook = XLSX.read(arrayBuffer, { type: 'array' });
  } catch (e) {
    throw new Error('Could not read this file - it may be corrupted or not a valid Excel workbook.');
  }

  if (workbook.vbaraw) {
    throw new Error('Macro-enabled workbooks are not supported. Please save as a plain .xlsx file.');
  }

  const spreadsheetAnswers = {};
  workbook.SheetNames.forEach((sheetName, index) => {
    const worksheet = workbook.Sheets[sheetName];
    const parsed = parseWorksheet(worksheet);
    if (!parsed) return; // skip genuinely empty sheets

    spreadsheetAnswers[`sheet${index}`] = {
      name: sheetName,
      gridJson: JSON.stringify({ grid: parsed.grid }),
      // Firestore rejects a raw array-of-arrays ("nested arrays are not
      // supported") - precomputedEval is a 2D grid, so it has to be
      // stringified the same way gridJson already is. Consumers
      // (generateSpreadsheetUploadPdf.js, FirebaseMarker.jsx) JSON.parse it
      // back before use.
      precomputedEvalJson: JSON.stringify(parsed.precomputedEval)
    };
  });

  if (Object.keys(spreadsheetAnswers).length === 0) {
    throw new Error('This spreadsheet appears to be empty.');
  }

  return spreadsheetAnswers;
}
